import { useEffect, useState } from 'react'
import { useAuth } from '../contexts/AuthContext'
import { userApi, UserProfile, publicApi, PricingPlan } from '../services/api'
import { User, Mail, Building, FileText, Lock, Eye, EyeOff, Save, RotateCcw, CheckCircle2, Shield, Calendar } from 'lucide-react'
import toast from 'react-hot-toast'
import { SubscriptionManager } from '../components/SubscriptionManager'

export function ProfilePage() {
  const { user } = useAuth()
  const [profile, setProfile] = useState<UserProfile | null>(null)
  const [loading, setLoading] = useState(true)
  const [updating, setUpdating] = useState(false)
  const [subscriptionInfo, setSubscriptionInfo] = useState<{
    userTier: string
    subscriptionStatus: string | null
    scheduledCancellation: boolean
    scheduledChangeType: 'upgrade' | 'downgrade' | 'cancel' | null
    scheduledChangeTier: 'builder' | 'launch' | 'growth' | 'scale' | null
    scheduledChangeEffectiveDate: string | null
    subscriptionEndDate: string | null
    subscriptionStartDate: string | null
    paddleSubscriptionId: string | null
  } | null>(null)
  const [pricingPlans, setPricingPlans] = useState<PricingPlan[]>([])
  
  // Form state
  const [firstName, setFirstName] = useState('')
  const [lastName, setLastName] = useState('')
  const [companyName, setCompanyName] = useState('')
  const [usageDescription, setUsageDescription] = useState('')
  
  // Password change state
  const [showPasswordSection, setShowPasswordSection] = useState(false)
  const [oldPassword, setOldPassword] = useState('')
  const [newPassword, setNewPassword] = useState('')
  const [confirmNewPassword, setConfirmNewPassword] = useState('')
  const [showOldPassword, setShowOldPassword] = useState(false)
  const [showNewPassword, setShowNewPassword] = useState(false)
  const [changingPassword, setChangingPassword] = useState(false)

  useEffect(() => {
    loadProfile()
    loadSubscriptionInfo()
    publicApi.getPricingPlans().then(setPricingPlans).catch(() => {})
  }, [])

  const loadProfile = async () => {
    try {
      const profileData = await userApi.getProfile()
      setProfile(profileData)
      setFirstName(profileData.firstName || '')
      setLastName(profileData.lastName || '')
      setCompanyName(profileData.companyName || '')
      setUsageDescription(profileData.usageDescription || '')
    } catch (error) {
      console.error('Failed to load profile:', error)
      toast.error('Failed to load profile information')
    } finally {
      setLoading(false)
    }
  }

  const loadSubscriptionInfo = async () => {
    try {
      const data = await userApi.getSubscriptionStatus()
      setSubscriptionInfo(data)
    } catch (error) {
      console.error('Failed to load subscription info:', error)
    }
  }

  const handleUpdateProfile = async (e: React.FormEvent) => {
    e.preventDefault()
    setUpdating(true)

    try {
      const updates: Partial<UserProfile> = {}
      
      if (firstName !== profile?.firstName) updates.firstName = firstName
      if (lastName !== profile?.lastName) updates.lastName = lastName
      if (companyName !== profile?.companyName) updates.companyName = companyName
      if (usageDescription !== profile?.usageDescription) updates.usageDescription = usageDescription

      if (Object.keys(updates).length === 0) {
        toast('No changes to save', { icon: 'ℹ️' })
        setUpdating(false)
        return
      }

      const updatedProfile = await userApi.updateProfile(updates)
      setProfile(updatedProfile)
      toast.success('Profile updated successfully!')
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Failed to update profile')
    } finally {
      setUpdating(false)
    }
  }

  const hasChanges = () => {
    return (
      firstName !== (profile?.firstName || '') ||
      lastName !== (profile?.lastName || '') ||
      companyName !== (profile?.companyName || '') ||
      usageDescription !== (profile?.usageDescription || '')
    )
  }

  const handleChangePassword = async (e: React.FormEvent) => {
    e.preventDefault()
    
    if (newPassword !== confirmNewPassword) {
      toast.error('New passwords do not match')
      return
    }

    if (newPassword.length < 8) {
      toast.error('Password must be at least 8 characters')
      return
    }

    setChangingPassword(true)

    try {
      await userApi.changePassword(oldPassword, newPassword)
      toast.success('Password changed successfully!')
      
      // Reset form
      setOldPassword('')
      setNewPassword('')
      setConfirmNewPassword('')
      setShowPasswordSection(false)
    } catch (error: any) {
      const errorMessage = error.response?.data?.error || error.response?.data?.message || 'Failed to change password'
      toast.error(errorMessage)
    } finally {
      setChangingPassword(false)
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner h-12 w-12"></div>
      </div>
    )
  }

  return (
    <div className="max-w-6xl mx-auto space-y-8 pb-12 animate-fade-in-up">
      {/* Header */}
      <div>
        <h1 className="text-3xl sm:text-4xl font-bold text-text mb-2">
          <span className="text-gradient">Account Settings</span>
        </h1>
        <p className="text-text-muted">
          Manage your profile, subscription, and security settings
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Content */}
        <div className="lg:col-span-2 space-y-6">
          {/* Profile Information Card */}
          <div className="card">
            <div className="p-6 border-b border-border">
              <h2 className="text-xl font-semibold text-text flex items-center gap-2">
                <User className="h-5 w-5 text-accent" />
                Profile Information
              </h2>
              <p className="mt-1 text-sm text-text-muted">
                Update your personal and company information
              </p>
            </div>
            
            <form onSubmit={handleUpdateProfile} className="p-6 space-y-6">
              {/* Email (Read-only) */}
              <div>
                <label className="block text-sm font-medium text-text mb-2 flex items-center gap-2">
                  <Mail className="h-4 w-4 text-accent" />
                  Email Address
                </label>
                <input
                  type="email"
                  readOnly
                  value={profile?.email || user?.signInDetails?.loginId || ''}
                  className="input-field opacity-60 cursor-not-allowed"
                />
                <p className="mt-1 text-xs text-text-muted">
                  Your email address cannot be changed
                </p>
              </div>

              {/* Name Fields */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label htmlFor="firstName" className="block text-sm font-medium text-text mb-2">
                    First Name
                  </label>
                  <input
                    id="firstName"
                    type="text"
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    placeholder="Enter your first name"
                    className="input-field"
                  />
                </div>
                
                <div>
                  <label htmlFor="lastName" className="block text-sm font-medium text-text mb-2">
                    Last Name
                  </label>
                  <input
                    id="lastName"
                    type="text"
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                    placeholder="Enter your last name"
                    className="input-field"
                  />
                </div>
              </div>

              {/* Company Name */}
              <div>
                <label htmlFor="companyName" className="block text-sm font-medium text-text mb-2 flex items-center gap-2">
                  <Building className="h-4 w-4 text-accent-2" />
                  Company Name
                </label>
                <input
                  id="companyName"
                  type="text"
                  value={companyName}
                  onChange={(e) => setCompanyName(e.target.value)}
                  placeholder="Enter your company name"
                  className="input-field"
                />
              </div>

              {/* Usage Description */}
              <div>
                <label htmlFor="usageDescription" className="block text-sm font-medium text-text mb-2 flex items-center gap-2">
                  <FileText className="h-4 w-4 text-accent" />
                  Usage Description
                </label>
                <textarea
                  id="usageDescription"
                  value={usageDescription}
                  onChange={(e) => setUsageDescription(e.target.value)}
                  placeholder="Describe how you plan to use the API"
                  rows={3}
                  className="input-field resize-none"
                />
                <p className="mt-1 text-xs text-text-muted">
                  Help us understand your use case
                </p>
              </div>

              {/* Action Buttons */}
              {hasChanges() && (
                <div className="flex justify-end gap-3 pt-4 border-t border-border">
                  <button
                    type="button"
                    onClick={loadProfile}
                    disabled={updating}
                    className="px-5 py-2.5 bg-transparent hover:bg-panel border border-border text-text-muted hover:text-text rounded-lg font-medium transition-all duration-200 flex items-center gap-2"
                  >
                    <RotateCcw className="h-4 w-4" />
                    Reset
                  </button>
                  <button
                    type="submit"
                    disabled={updating}
                    className="px-5 py-2.5 bg-accent hover:bg-accent/90 text-white rounded-lg font-medium transition-all duration-200 flex items-center gap-2 disabled:opacity-50 shadow-lg shadow-accent/20"
                  >
                    {updating ? (
                      <>
                        <div className="spinner h-4 w-4" />
                        Saving...
                      </>
                    ) : (
                      <>
                        <Save className="h-4 w-4" />
                        Save Changes
                      </>
                    )}
                  </button>
                </div>
              )}
            </form>
          </div>

          {/* Password Management Card */}
          <div className="card">
            <div className="p-6 border-b border-border">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-xl font-semibold text-text flex items-center gap-2">
                    <Lock className="h-5 w-5 text-accent" />
                    Password & Security
                  </h2>
                  <p className="mt-1 text-sm text-text-muted">
                    Update your password to keep your account secure
                  </p>
                </div>
                {!showPasswordSection && (
                  <button
                    type="button"
                    onClick={() => setShowPasswordSection(true)}
                    className="px-4 py-2 bg-accent/10 hover:bg-accent/20 text-accent border border-accent/30 rounded-lg font-medium transition-all duration-200"
                  >
                    Change Password
                  </button>
                )}
              </div>
            </div>

            {showPasswordSection && (
              <form onSubmit={handleChangePassword} className="p-6 space-y-5">
                <div>
                  <label htmlFor="oldPassword" className="block text-sm font-medium text-text mb-2">
                    Current Password
                  </label>
                  <div className="relative">
                    <input
                      id="oldPassword"
                      type={showOldPassword ? 'text' : 'password'}
                      value={oldPassword}
                      onChange={(e) => setOldPassword(e.target.value)}
                      placeholder="Enter current password"
                      className="input-field pr-10"
                      required
                    />
                    <button
                      type="button"
                      className="absolute inset-y-0 right-0 pr-3 flex items-center text-text-muted hover:text-text"
                      onClick={() => setShowOldPassword(!showOldPassword)}
                    >
                      {showOldPassword ? (
                        <EyeOff className="h-4 w-4" />
                      ) : (
                        <Eye className="h-4 w-4" />
                      )}
                    </button>
                  </div>
                </div>

                <div>
                  <label htmlFor="newPassword" className="block text-sm font-medium text-text mb-2">
                    New Password
                  </label>
                  <div className="relative">
                    <input
                      id="newPassword"
                      type={showNewPassword ? 'text' : 'password'}
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      placeholder="Enter new password"
                      className="input-field pr-10"
                      required
                    />
                    <button
                      type="button"
                      className="absolute inset-y-0 right-0 pr-3 flex items-center text-text-muted hover:text-text"
                      onClick={() => setShowNewPassword(!showNewPassword)}
                    >
                      {showNewPassword ? (
                        <EyeOff className="h-4 w-4" />
                      ) : (
                        <Eye className="h-4 w-4" />
                      )}
                    </button>
                  </div>
                  <p className="mt-1 text-xs text-text-muted">
                    At least 8 characters with uppercase, lowercase, and numbers
                  </p>
                </div>

                <div>
                  <label htmlFor="confirmNewPassword" className="block text-sm font-medium text-text mb-2">
                    Confirm New Password
                  </label>
                  <input
                    id="confirmNewPassword"
                    type="password"
                    value={confirmNewPassword}
                    onChange={(e) => setConfirmNewPassword(e.target.value)}
                    placeholder="Confirm new password"
                    className="input-field"
                    required
                  />
                  {newPassword !== confirmNewPassword && confirmNewPassword && (
                    <p className="mt-1 text-sm text-destructive">Passwords do not match</p>
                  )}
                </div>

                <div className="flex justify-end gap-3 pt-4 border-t border-border">
                  <button
                    type="button"
                    onClick={() => {
                      setShowPasswordSection(false)
                      setOldPassword('')
                      setNewPassword('')
                      setConfirmNewPassword('')
                    }}
                    className="px-5 py-2.5 bg-transparent hover:bg-panel border border-border text-text-muted hover:text-text rounded-lg font-medium transition-all duration-200"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={changingPassword || newPassword !== confirmNewPassword || !oldPassword || !newPassword}
                    className="px-5 py-2.5 bg-accent hover:bg-accent/90 text-white rounded-lg font-medium transition-all duration-200 flex items-center gap-2 disabled:opacity-50 shadow-lg shadow-accent/20"
                  >
                    {changingPassword ? (
                      <>
                        <div className="spinner h-4 w-4" />
                        Updating...
                      </>
                    ) : (
                      <>
                        <CheckCircle2 className="h-4 w-4" />
                        Update Password
                      </>
                    )}
                  </button>
                </div>
              </form>
            )}

            {!showPasswordSection && (
              <div className="p-6 text-center text-sm text-text-muted">
                Click "Change Password" to update your password
              </div>
            )}
          </div>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Subscription Card */}
          <div className="card">
            <div className="p-6 border-b border-border">
              <h2 className="text-xl font-semibold text-text flex items-center gap-2">
                <Shield className="h-5 w-5 text-accent-2" />
                Subscription
              </h2>
            </div>
            <div className="p-6">
              <SubscriptionManager 
                subscriptionInfo={subscriptionInfo} 
                onRefresh={loadSubscriptionInfo}
                pricingPlans={pricingPlans}
              />
            </div>
          </div>

          {/* Account Info Card */}
          <div className="card p-6">
            <h3 className="text-sm font-semibold text-text mb-4 flex items-center gap-2">
              <Calendar className="h-4 w-4 text-accent" />
              Account Information
            </h3>
            <div className="space-y-3 text-sm">
              <div className="flex items-center justify-between py-3 border-b border-border">
                <span className="text-text-muted">Member Since</span>
                <span className="font-medium text-text">
                  {profile?.createdAt ? new Date(profile.createdAt).toLocaleDateString('en-US', { month: 'short', year: 'numeric' }) : 'N/A'}
                </span>
              </div>
              <div className="flex items-center justify-between py-3">
                <span className="text-text-muted">Last Updated</span>
                <span className="font-medium text-text">
                  {profile?.updatedAt ? new Date(profile.updatedAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : 'N/A'}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
